import { memo, useCallback, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";

// ─── Types ────────────────────────────────────────────────────────
export type ModalActionVariant =
  "default" | "primary" | "warning" | "danger" | "success" | "info";

export interface ModalAction {
  key: string;
  label: string;
  icon: ReactNode;
  variant?: ModalActionVariant;
  onClick: () => void;
  hidden?: boolean;
  disabled?: boolean;
}

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

export interface ModalStatus {
  label: string;
  variant:
    | "active"
    | "draft"
    | "approved"
    | "archived"
    | "pending"
    | "declined"
    | "custom";
  className?: string;
}

export interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  status?: ModalStatus;
  actions?: ModalAction[];
  /** Icon actions rendered directly after the status badge. */
  statusActions?: ModalAction[];
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  /** When false, hide the small default close button in the header. */
  showCloseButton?: boolean;
  className?: string;
}

// ─── Style maps ───────────────────────────────────────────────────
const ACTION_CLASSES: Record<ModalActionVariant, string> = {
  default:
    "bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600",
  primary: "text-white hover:opacity-90",
  warning: "bg-amber-500 text-white hover:bg-amber-600",
  danger: "bg-red-500 text-white hover:bg-red-600",
  success: "bg-emerald-500 text-white hover:bg-emerald-600",
  info: "bg-sky-500 text-white hover:bg-sky-600",
};

const STATUS_CLASSES: Record<ModalStatus["variant"], string> = {
  active:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  approved:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  draft: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  pending:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  archived:
    "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400",
  custom: "",
};

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  full: "max-w-5xl",
};

// ─── Pre-built icons ──────────────────────────────────────────────
export const MODAL_ICONS = {
  edit: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z" />
      <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z" />
    </svg>
  ),
  delete: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  view: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
      <path
        fillRule="evenodd"
        d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  add: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
    </svg>
  ),
  approve: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  decline: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  ),
  share: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.475l6.733-3.366A2.52 2.52 0 0 1 13 4.5Z" />
    </svg>
  ),
  download: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
      <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
    </svg>
  ),
  save: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M5 3h11l3 3v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M7 3v5h8V3" />
      <path d="M7 21v-7h10v7" />
    </svg>
  ),
  load: (
    <svg
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      className="hds-flight-icon--animation-loading"
    >
      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        {" "}
        <g fill="#000000" fill-rule="evenodd" clip-rule="evenodd">
          {" "}
          <path
            d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8z"
            opacity=".2"
          ></path>{" "}
          <path d="M7.25.75A.75.75 0 018 0a8 8 0 018 8 .75.75 0 01-1.5 0A6.5 6.5 0 008 1.5a.75.75 0 01-.75-.75z"></path>{" "}
        </g>{" "}
      </g>
    </svg>
  ),
} as const;

// ─── Component ────────────────────────────────────────────────────
const AppModal = memo(function AppModal({
  isOpen,
  onClose,
  title,
  status,
  actions = [],
  statusActions = [],
  children,
  footer,
  size = "md",
  closeOnBackdrop = true,
  closeOnEsc = true,
  className,
  showCloseButton = true,
}: AppModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [isOpen, closeOnEsc, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  const handleBackdrop = useCallback(() => {
    if (closeOnBackdrop) onClose();
  }, [closeOnBackdrop, onClose]);

  if (!isOpen) return null;

  const visibleActions = actions?.filter((a) => !a.hidden);

  return createPortal(
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      role="dialog"
      aria-modal
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleBackdrop}
        aria-hidden
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "relative z-10 w-full animate-scale-in",
          "rounded-2xl bg-white shadow-modal outline-none dark:bg-neutral-900",
          "flex max-h-[90vh] flex-col",
          SIZE_CLASSES[size],
          className,
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4 dark:border-neutral-800">
          <div className="flex min-w-0 items-center gap-2.5">
            {title && (
              <h2 className="truncate text-base font-semibold text-neutral-900 dark:text-neutral-50">
                {title}
              </h2>
            )}
            {status && (
              <span
                className={cn(
                  "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide",
                  status.variant === "custom"
                    ? status.className
                    : STATUS_CLASSES[status.variant],
                )}
              >
                {status.label}
              </span>
            )}
            {statusActions
              .filter((a) => !a.hidden)
              .map((action) => (
                <button
                  key={action.key}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  aria-label={action.label}
                  title={action.label}
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-50",
                    ACTION_CLASSES[action.variant ?? "default"],
                  )}
                >
                  {action.icon}
                </button>
              ))}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {visibleActions.map((action) => {
              // derive fallback icons and colors for common action keys when icon is not provided
              let icon = action.icon;
              let extraClass = ACTION_CLASSES[action.variant ?? "default"];
              let inlineStyle: React.CSSProperties | undefined = undefined;

              const keyLower = (action.key || action.label || "").toString().toLowerCase();

              if (!icon) {
                if (/(close|cancel)/.test(keyLower)) {
                  icon = MODAL_ICONS.close;
                  extraClass = "bg-amber-500 text-white hover:bg-amber-600";
                } else if (/(save|download)/.test(keyLower)) {
                  icon = /save/.test(keyLower) ? MODAL_ICONS.save : MODAL_ICONS.download;
                  extraClass = "bg-sky-700 text-white hover:bg-sky-800";
                } else if (/(submit|send|share|plane)/.test(keyLower)) {
                  icon = MODAL_ICONS.share;
                  extraClass = "bg-violet-700 text-white hover:bg-violet-800";
                } else if (/(confirm|accept|approve)/.test(keyLower)) {
                  icon = MODAL_ICONS.approve;
                  extraClass = "bg-emerald-500 text-white hover:bg-emerald-600";
                }
              }

              // Save/download actions are blue unless a variant is set explicitly, even when an icon is supplied.
              if (/(save|download)/.test(keyLower) && !action.variant) {
                extraClass = "bg-sky-700 text-white hover:bg-sky-800";
              }

              // primary variant preserves the existing behaviour (uses sidebar color)
              const isPrimary = action.variant === "primary";

              return (
                <button
                  key={action.key}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  aria-label={action.label}
                  title={action.label}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-50",
                    isPrimary ? "text-white" : extraClass,
                  )}
                  style={isPrimary ? { backgroundColor: "var(--sidebar-bg)" } : inlineStyle}
                >
                  {icon}
                </button>
              );
            })}
            {showCloseButton && (
              <button
                onClick={onClose}
                aria-label="Cancel"
                title="Cancel"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-white transition-colors hover:bg-amber-600"
              >
                {MODAL_ICONS.close}
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 border-t border-neutral-100 px-5 py-4 dark:border-neutral-800">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
});

export default AppModal;
